/**
 * StatusBadge Component
 * 
 * A reusable component for displaying status indicators with colored circular badges.
 * Used throughout the application to show status information (Active, At Risk, In Review, Completed, etc.)
 * 
 * Design Principles:
 * - Circular badge with colored background
 * - White text for contrast
 * - Consistent sizing and spacing
 * - Accessible with proper ARIA labels
 */

import React from 'react';
import './StatusBadge.css';

export interface StatusBadgeProps {
  /** The status text to display */
  status: string;
  /** The color variant for the badge */
  variant?: 'active' | 'at-risk' | 'in-review' | 'completed' | 'pending' | 'open' | 'closed';
  /** Custom color (overrides variant) */
  color?: string;
  /** Size of the badge */
  size?: 'small' | 'medium' | 'large';
  /** Additional CSS class name */
  className?: string;
}

/**
 * Maps status variants to their corresponding colors
 * Following the design system: green for active, red for at-risk, blue for in-review, gray for completed
 */
const getStatusColor = (variant?: string, customColor?: string): string => {
  if (customColor) return customColor;
  
  const colorMap: Record<string, string> = {
    'active': '#28a745',      // Green
    'at-risk': '#dc3545',     // Red
    'in-review': '#17a2b8',   // Blue/Cyan
    'completed': '#6c757d',   // Gray
    'pending': '#ffc107',     // Yellow/Amber
    'open': '#28a745',        // Green
    'closed': '#6c757d',      // Gray
  };
  
  return colorMap[variant || ''] || '#545465'; // Default gray
};

/**
 * Determines variant from status text if not explicitly provided
 */
const getVariantFromStatus = (status: string): string => {
  const statusLower = status.toLowerCase();
  if (statusLower.includes('active') || statusLower.includes('open')) return 'active';
  if (statusLower.includes('risk') || statusLower.includes('overdue')) return 'at-risk';
  if (statusLower.includes('review') || statusLower.includes('progress')) return 'in-review';
  if (statusLower.includes('complete') || statusLower.includes('closed')) return 'completed';
  if (statusLower.includes('pending')) return 'pending';
  return 'open';
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  variant,
  color,
  size = 'medium',
  className = '',
}) => {
  // Auto-detect variant from status if not provided
  const finalVariant = variant || getVariantFromStatus(status);
  const badgeColor = getStatusColor(finalVariant, color);

  // Get light background color class
  const backgroundClass = `status-badge-variant-${finalVariant}`;
  
  return (
    <div
      className={`status-badge status-badge-${size} ${backgroundClass} ${className}`}
      role="status"
      aria-label={`Status: ${status}`}
    >
      <span className="status-badge-dot" style={{ backgroundColor: badgeColor }} />
      <span className="status-badge-text">{status}</span>
    </div>
  );
};

export default StatusBadge;

